import React, { createContext, useCallback, useContext, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { DbItemAdapter, PaginatedItems } from '@makyoapp/makyo-common'
import { useQueryParam } from '@/shared/makyo-hooks'

import { queryKeys } from '@/lib/react-query/query-keys'
import { useGetOrgDatabases } from '@/resolvers/db/query'

import { useParamId } from '../hooks/useParamId'
import { useCurrentOrgContext } from './CurrentOrgContext'
import { useSingleProjectContext } from './SingleProjectContext'

/**
 * State
 */
interface DbState {
  allItems: DbItemAdapter[] | null
  setAllItems: (cb: DbItemAdapter[] | ((prev: DbItemAdapter[]) => DbItemAdapter[])) => void
  currentItems: PaginatedItems<DbItemAdapter>
  setCurrentItems: (nextItems: PaginatedItems<DbItemAdapter>) => void
  isLoading: boolean
  page: number
  setPage: (nextPage: number) => void
  search: string
  debounceSearch: string
  setSearch: (keyword: string) => void
  resetParams: () => void
  handleList: (options?: { onSuccess?: () => void; onFinally?: () => void }) => void
  updateDb: (dbId: string, cb: (db: DbItemAdapter) => DbItemAdapter) => void
}

const DbContext = createContext<DbState>({
  allItems: [],
  setAllItems: () => null,
  currentItems: {
    items: [],
    totalCount: 0,
  },
  setCurrentItems: () => null,
  isLoading: false,
  page: 1,
  setPage: () => null,
  search: '',
  setSearch: () => null,
  debounceSearch: '',
  resetParams: () => null,
  handleList: () => null,
  updateDb: () => null,
})

export function useDbContext() {
  return useContext(DbContext)
}

/**
 * DbContext provider
 */
export const DbProvider = ({ children }: { children?: React.ReactNode }) => {
  const { currentOrg } = useCurrentOrgContext()

  const queryClient = useQueryClient()

  const dbQuery = useGetOrgDatabases()
  const databases = dbQuery.data || []
  const isLoading = dbQuery.isLoading

  const [currentItems, setCurrentItems] = useState<PaginatedItems<DbItemAdapter>>({
    items: [],
    totalCount: 0,
  })

  const isProjectPage = useParamId('project') !== undefined
  const { project } = useSingleProjectContext()

  const orgId = isProjectPage && project ? project.org.id : currentOrg?.id
  const { page, setPage, search, setSearch, debounceSearch, resetParams } = useQueryParam()

  const handleList = useCallback(
    (options?: { onSuccess?: () => void; onFinally?: () => void }) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.orgDatabases, orgId] })
    },
    [orgId, queryClient]
  )

  const setAllItems = useCallback(
    (cb: DbItemAdapter[] | ((prev: DbItemAdapter[]) => DbItemAdapter[])) => {
      queryClient.setQueryData([queryKeys.orgDatabases, orgId], cb)
    },
    [orgId, queryClient]
  )

  const updateDb = useCallback(
    (dbId: string, updateCb: (db: DbItemAdapter) => DbItemAdapter) => {
      setAllItems((prev) => {
        if (prev) {
          const copy = [...prev]
          const idx = copy.findIndex((d) => d.id === dbId)
          if (idx !== -1) {
            const d = copy[idx]
            copy[idx] = updateCb(d)
          }
          return copy
        }
        return prev
      })
    },
    [setAllItems]
  )

  return (
    <DbContext.Provider
      value={{
        allItems: databases,
        setAllItems,
        currentItems,
        setCurrentItems,
        isLoading,
        page,
        setPage,
        search,
        setSearch,
        debounceSearch,
        resetParams,
        handleList,
        updateDb,
      }}
    >
      {children}
    </DbContext.Provider>
  )
}
